import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { Pick } from "@/components/vi/SaleDialog";
import { QuickForm } from "@/components/vi/ViDialogs";
import { Plus, Trash2, Check } from "lucide-react";
import { call, money } from "@/lib/vi";

export function Card({ title, children, actions, testid }) {
  return (
    <div className="rounded-xl border bg-white p-4 mb-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap"><p className="font-head font-semibold text-sm">{title}</p><div className="flex gap-2 flex-wrap">{actions}</div></div>
      {children}
    </div>
  );
}

export function MiniTable({ head, rows, testid }) {
  if (!rows.length) return <p className="text-xs text-slate-400">Aucun élément</p>;
  return (
    <div className="overflow-x-auto scrollbar-thin" data-testid={testid}>
      <Table><TableHeader><TableRow className="bg-slate-50">{head.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
        <TableBody>{rows.map((r, i) => <TableRow key={i}>{r.map((c, j) => <TableCell key={j} className="text-xs">{c}</TableCell>)}</TableRow>)}</TableBody></Table>
    </div>
  );
}

export function StockPrep({ tour, reload }) {
  const [arts, setArts] = useState([]);
  const [lines, setLines] = useState(tour.stock_lines.map((l) => ({ article_id: l.article_id, quantite_demandee: l.quantite_demandee, quantite_preparee: l.quantite_preparee })));
  useEffect(() => { api.get("/articles").then((r) => setArts(r.data)); }, []);
  const upd = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const save = () => call(() => api.put(`/itinerant/tournees/${tour.id}/stock`, { lines: lines.filter((l) => l.article_id) }), "Préparation enregistrée").then((r) => r && reload());
  const validate = () => call(() => api.post(`/itinerant/tournees/${tour.id}/stock/validate`), "Stock transféré vers le tricycle").then((r) => r && reload());
  const total = tour.stock_lines.reduce((s, l) => s + l.valeur, 0);
  return (
    <Card title={`Préparation du stock — entrepôt ${tour.entrepot_name}`} testid="stock-prep"
      actions={<><Button size="sm" variant="outline" onClick={save} data-testid="stock-save">Enregistrer</Button>
        {tour.statut === "Préparation" && <Button size="sm" onClick={validate} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="stock-validate"><Check className="h-4 w-4 mr-1" />Valider et transférer</Button>}</>}>
      <div className="space-y-2">
        {lines.map((l, i) => {
          const saved = tour.stock_lines.find((s) => s.article_id === l.article_id);
          return (
            <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`prep-line-${i}`}>
              <div className="col-span-12 sm:col-span-6"><Pick value={l.article_id} onChange={(v) => upd(i, "article_id", v)} placeholder="Article" testid={`prep-article-${i}`}
                options={arts.map((a) => ({ value: a.id, label: `${a.reference} · ${a.designation} — ${money(a.prix_vente)}` }))} /></div>
              <div className="col-span-5 sm:col-span-2"><Input type="number" placeholder="Demandée" value={l.quantite_demandee} onChange={(e) => upd(i, "quantite_demandee", Number(e.target.value))} data-testid={`prep-qd-${i}`} /></div>
              <div className="col-span-5 sm:col-span-2"><Input type="number" placeholder="Préparée" value={l.quantite_preparee ?? ""} onChange={(e) => upd(i, "quantite_preparee", Number(e.target.value))} data-testid={`prep-qp-${i}`} /></div>
              <div className="col-span-1 text-[10px] text-slate-500">{saved ? `dispo ${saved.disponible}` : ""}</div>
              <button className="col-span-1 text-slate-400 hover:text-rose-500" onClick={() => setLines(lines.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></button>
            </div>
          );
        })}
        <Button size="sm" variant="outline" onClick={() => setLines([...lines, { article_id: "", quantite_demandee: 1, quantite_preparee: 1 }])} data-testid="prep-add"><Plus className="h-4 w-4 mr-1" />Article</Button>
        {tour.stock_lines.length > 0 && <p className="text-xs text-right">Valeur préparée : <b className="font-mono" data-testid="prep-total">{money(total)}</b></p>}
      </div>
    </Card>
  );
}

export function StockRemis({ tour }) {
  return (
    <div data-testid="stock-remis">
      <p className="text-xs uppercase font-mono text-slate-500 mb-1">Stock remis</p>
      <MiniTable head={["Produit", "Quantité", "Prix", "Valeur"]} rows={tour.stock_lines.map((l) => [l.designation, l.quantite_preparee, money(l.prix), money(l.valeur)])} />
      <p className="text-sm text-right mt-1">Valeur totale : <b>{money(tour.stock_valeur)}</b></p>
    </div>
  );
}

export function ReceptionDialog({ tour, open, onOpenChange, reload }) {
  return <QuickForm open={open} onOpenChange={onOpenChange} title="Prise de stock" testid="reception-dialog" initial={{ accepte: false }}
    fields={[{ name: "accepte", label: "Je reconnais avoir reçu le stock indiqué ci-dessus.", type: "checkbox" }, { name: "password", label: "Validation par code (votre mot de passe)", type: "password" }]}
    onSubmit={async (f) => { const r = await call(() => api.post(`/itinerant/tournees/${tour.id}/reception`, f), "Réception confirmée"); if (r) reload(); return !!r; }}>
    <StockRemis tour={tour} />
  </QuickForm>;
}

export function DepartDialog({ tour, open, onOpenChange, reload }) {
  return <QuickForm open={open} onOpenChange={onOpenChange} title="Départ en tournée" testid="depart-dialog" submitLabel="DÉMARRER LA TOURNÉE"
    initial={{ kilometrage: 0, carburant: "Plein", etat: "Bon", caisse_initiale: 0, confirm_stock: false, confirm_conducteur: false }}
    fields={[{ name: "kilometrage", label: "Kilométrage départ", type: "number" }, { name: "carburant", label: "Niveau carburant", type: "select", options: ["Plein", "3/4", "1/2", "1/4", "Réserve"] },
      { name: "etat", label: "État général du tricycle", type: "select", options: ["Bon", "Moyen", "À surveiller"] }, { name: "caisse_initiale", label: "Caisse initiale (FCFA)", type: "number" },
      { name: "confirm_stock", label: "Je confirme le stock chargé", type: "checkbox" }, { name: "confirm_conducteur", label: "Je confirme être le conducteur de ce tricycle", type: "checkbox" }]}
    onSubmit={async (f) => { const r = await call(() => api.post(`/itinerant/tournees/${tour.id}/depart`, f), "Tournée démarrée"); if (r) reload(); return !!r; }} />;
}

export function RetourDialog({ tour, open, onOpenChange, reload }) {
  return <QuickForm open={open} onOpenChange={onOpenChange} title="Retour au dépôt" testid="retour-dialog" initial={{ kilometrage: tour.depart?.kilometrage || 0 }}
    fields={[{ name: "kilometrage", label: "Kilométrage retour", type: "number" }]}
    onSubmit={async (f) => { const r = await call(() => api.post(`/itinerant/tournees/${tour.id}/retour`, f), "Retour enregistré"); if (r) reload(); return !!r; }} />;
}

export function MobileStock({ tour }) {
  return <MiniTable testid="mobile-stock" head={["Article", "Catégorie", "Chargé", "Vendu", "Retourné", "Restant", "Prix", "Valeur"]}
    rows={tour.mobile_stock.map((s) => [s.designation, s.categorie, s.charge, s.vendu, s.retourne, <b key="r">{s.restant}</b>, money(s.prix), money(s.valeur)])} />;
}

export function SalesTable({ tour, canCancel, reload }) {
  const cancel = async (s) => {
    const motif = window.prompt(`Motif d'annulation de ${s.numero}`);
    if (motif) { const r = await call(() => api.post(`/itinerant/sales/${s.id}/cancel`, { motif }), "Vente annulée"); if (r) reload(); }
  };
  return <MiniTable testid="tour-sales" head={["N°", "Client", "Total", "Encaissé", "Crédit", "Facture", "Statut", ""]}
    rows={tour.sales.map((s) => [s.numero, s.client_name, money(s.total), money(s.paye), money(s.credit), s.invoice_numero, <StatusBadge key="s" value={s.statut} />,
      canCancel && s.statut === "Validée" ? <Button key="c" size="sm" variant="ghost" className="text-rose-600 h-7" onClick={() => cancel(s)} data-testid={`sale-cancel-${s.numero}`}>Annuler</Button> : ""])} />;
}
